import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import { ListButton } from "~/components/ListView/ListButton";
import { SelectTaskLabels } from "~/components/tasks/TaskOptions/Submenus";
import { editTask } from "~/store/actions/actions.tasks";
import { getTaskById } from "~/store/selectors/selector.tasks";
import { hasCollaborativePermissionsOnProject } from "~/store/selectors/selector.users";
import LabelItems from "./LabelItems";
import { LabelTrigger } from "./LabelTrigger";
import { propTypes } from "./propTypes";

export const Label = ({ taskId, projectId }) => {
  const dispatch = useDispatch();
  const task = useSelector((state) => getTaskById(state, taskId));
  const canEdit = useSelector((state) => hasCollaborativePermissionsOnProject(state, projectId));
  const triggerRef = useRef();

  const defaultLabels = useMemo(() => {
    return task?.labels.toJS() || [];
  }, [task?.labels]);

  const [labels, setLabels] = useState(() => defaultLabels);

  useEffect(() => {
    setLabels(defaultLabels);
  }, [defaultLabels]);

  const onChange = useCallback((args) => {
    setLabels(args?.labels);
  }, []);

  const handleSave = useCallback(() => {
    if (!isEqual(defaultLabels.sort(), labels.sort())) {
      dispatch(editTask(projectId, taskId, { labels }));
    }
  }, [defaultLabels, dispatch, labels, projectId, taskId]);

  if (!canEdit && labels.length === 0) {
    return null;
  }

  if ((!canEdit && labels.length > 0) || task?.get("is_completed")) {
    return (
      <div className="tw-flex tw-px-2">
        <LabelItems labels={labels} />
      </div>
    );
  }

  return (
    <SelectTaskLabels
      projectId={projectId}
      selected={labels}
      onChange={onChange}
      position="bottom-end"
      selectClassName="tw-mx-2"
      onClose={handleSave}
      target={
        <ListButton
          variant="text gray"
          className={classNames("tw-flex tw-justify-end tw-py-1 tw-px-2 tw-overflow-visible", {
            "tw-w-auto tw-max-w-full": labels.length > 0,
            "tw-w-auto show-on-hover": !labels.length,
          })}
          ref={triggerRef}
          data-testid="task-list-labels"
        >
          <LabelTrigger labels={labels} />
        </ListButton>
      }
    />
  );
};

Label.propTypes = propTypes;
